'use client';

import { useCheckboxGroup } from '@react-aria/checkbox';
import { useCheckboxGroupState } from '@react-stately/checkbox';

import { type VariantProps } from 'tailwind-variants';

import { FormDescription } from '../../internal/components/form/FormDescription';
import { FormLabel } from '../../internal/components/form/FormLabel';
import { filterProps } from '../../internal/utils/filterProps';
import { type Component, type ComponentProps } from '../../types/component';
import { mergeProps } from '../../utils/mergeProps';
import { tv } from '../../utils/tv';
import { type CheckboxProps } from '../checkbox/Checkbox';
import { CheckboxGroupContext } from './CheckboxGroupContext';

type Variant = VariantProps<typeof checkboxGroupStyles>;
interface CheckboxGroupBaseProps {
  /**
   * The label for the `CheckboxGroup`.
   */
  label?: string;
  /**
   * The current selected values (controlled).
   */
  value?: string[];
  /**
   * The default selected values (uncontrolled).
   */
  defaultValue?: string[];
  /**
   * Handler that is called when the selected values change.
   */
  onChange?: (value: string[]) => void;
  /**
   * Whether all checkboxes in the group are disabled.
   */
  isDisabled?: boolean;
  /**
   * Whether the group is invalid.
   */
  isInvalid?: Variant['isInvalid'];
  /**
   * Provide the user with context or feedback for the `CheckboxGroup`.
   * Shown below the group. Replaced by `errorMessage` when `isInvalid` is true and `errorMessage` is set.
   */
  description?: string;
  /**
   * The error message to display when `isInvalid` is true. Replaces `description` when shown.
   */
  errorMessage?: string;
  /**
   * Whether the `CheckboxGroup` is in a success state.
   */
  isSuccess?: boolean;
  /**
   * Success message shown when `isSuccess` is true. Replaces `description` when shown.
   */
  successMessage?: string;
  /**
   * Secondary content displayed after the label (e.g., "Optional").
   */
  subLabel?: string;
  /**
   * Content rendered at the end of the label row (e.g., character count).
   */
  endLabel?: string;
  /**
   * Whether a selection is required before form submission. Shows a required indicator on the label.
   */
  isRequired?: boolean;
  /**
   * Handler called when the focus state changes within the `CheckboxGroup`.
   */
  onFocusChange?: (isFocused: boolean) => void;
  /**
   * The color of the `CheckboxGroup` label text.
   * @default 'primary'
   */
  color?: Variant['color'];
  /**
   * The orientation of the `CheckboxGroup`.
   * @default 'vertical'
   */
  orientation?: Variant['orientation'];
  /**
   * The `<form>` element to associate the `CheckboxGroup` with. The value of this attribute must be the id of a `<form>` in the same document.
   */
  form?: string;
  /**
   * The name of the `CheckboxGroup`, used when submitting an HTML form.
   */
  name?: string;
  /**
   * The size of all child `Checkbox` components. Propagated via context; individual checkboxes may override this.
   * @default 'md'
   */
  size?: CheckboxProps['size'];
  /**
   * The icon position of all child `Checkbox` components. Propagated via context; individual checkboxes may override this.
   * @default 'left'
   */
  iconPosition?: CheckboxProps['iconPosition'];
  /**
   * The icon alignment of all child `Checkbox` components. Propagated via context; individual checkboxes may override this.
   * @default 'start'
   */
  alignIcon?: CheckboxProps['alignIcon'];
}

const checkboxGroupClasses = {
  root: 'zds-checkboxGroup-root',
  label: 'zds-checkboxGroup-label',
  description: 'zds-checkboxGroup-description',
} as const;

const checkboxGroupStyles = tv({
  slots: {
    root: [checkboxGroupClasses.root, 'flex'],
    label: [checkboxGroupClasses.label, 'flex', 'items-center'],
    description: [checkboxGroupClasses.description],
  },
  variants: {
    color: {
      primary: {
        label: 'text-primary',
      },
      white: {
        label: 'text-static-white-main',
      },
    },
    isInvalid: {
      true: {
        label: 'text-error-main',
      },
    },
    isDisabled: {
      true: {
        label: 'disabled-foreground',
      },
    },
    orientation: {
      vertical: { root: 'flex-col z--checkboxGroup-gap-vertical' },
      horizontal: {
        root: 'flex-row flex-wrap z--checkboxGroup-gap-horizontal',
        label: 'basis-full',
        description: 'basis-full',
      },
    },
  },
});

type CheckboxGroupProps = ComponentProps<CheckboxGroupBaseProps, 'div'>;

type CheckboxGroupComponent = Component<CheckboxGroupBaseProps, 'div'>;

const CheckboxGroup: CheckboxGroupComponent = (props) => {
  const [localProps, checkboxGroupRaProps] = filterProps(props, [
    'useCheckboxGroup',
  ]);

  // Component-specific props + restLocalProps for the DOM element
  const {
    children,
    subLabel,
    endLabel,
    isSuccess,
    successMessage,
    color = 'primary',
    orientation = 'vertical',
    size,
    iconPosition,
    alignIcon,
    className,
    ref,
    ...restLocalProps
  } = localProps;

  // Hook-claimed props also needed for rendering
  const { label, description, errorMessage, isDisabled, isRequired } = props;

  const state = useCheckboxGroupState(checkboxGroupRaProps);

  const {
    groupProps,
    labelProps,
    descriptionProps,
    errorMessageProps,
    isInvalid,
  } = useCheckboxGroup(checkboxGroupRaProps, state);

  const styles = checkboxGroupStyles({
    color,
    isInvalid,
    isDisabled,
    orientation,
  });

  const descriptionAriaProps =
    isInvalid && errorMessage ? errorMessageProps : descriptionProps;

  return (
    <div
      {...mergeProps(restLocalProps, groupProps)}
      ref={ref}
      className={styles.root({ className })}
    >
      <FormLabel
        {...labelProps}
        label={label}
        subLabel={subLabel}
        endLabel={endLabel}
        isRequired={isRequired}
        isDisabled={isDisabled}
        color={color}
        className={styles.label()}
      />

      <CheckboxGroupContext.Provider
        value={{ state, color, size, iconPosition, alignIcon, orientation }}
      >
        {children}
      </CheckboxGroupContext.Provider>

      <FormDescription
        {...descriptionAriaProps}
        description={description}
        errorMessage={errorMessage}
        successMessage={successMessage}
        isInvalid={isInvalid}
        isSuccess={isSuccess}
        isDisabled={isDisabled}
        color={color}
        className={styles.description()}
      />
    </div>
  );
};

CheckboxGroup.displayName = 'CheckboxGroup';

export {
  CheckboxGroup,
  type CheckboxGroupProps,
  type CheckboxGroupBaseProps,
  checkboxGroupClasses,
};
